import React, { useState } from 'react';
import { Button, Input, Popover, Radio } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import classNames from 'classnames';

interface ContactsFilterOption<T extends string> {
  value: T;
  label: string;
}

type ContactsFilterDropdownProps<T extends string> = {
  label: string;
  title: string;
  options: ContactsFilterOption<T>[];
} & (
  | { mode: 'multiple'; value: T[]; onChange: (value: T[]) => void }
  | { mode: 'single'; value: T; onChange: (value: T) => void }
  // A list with a star per option instead of radios, the star marks the default choice
  | { mode: 'starred'; value: T; onChange: (value: T) => void; starredValue: T; onStar: (value: T) => void; footer: string }
);

// Filter button opening a panel: chips with a search for the tag filters, radios for the single choices
const ContactsFilterDropdown = <T extends string>(props: ContactsFilterDropdownProps<T>) => {
  const { label, title, options } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const normalizedSearch = search.trim().toLowerCase();
  const visibleOptions = normalizedSearch
    ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
    : options;
  const selectedCount = props.mode === 'multiple' ? props.value.length : 0;

  const toggle = (value: T) => {
    if (props.mode !== 'multiple') return;
    props.onChange(props.value.includes(value) ? props.value.filter((v) => v !== value) : [...props.value, value]);
  };

  const content = (
    <div className="contacts__dropdown">
      <div className="contacts__dropdown-head">
        <span className="contacts__dropdown-title">{title}</span>
        <Button size="small" icon={<CloseOutlined />} aria-label="Fermer" onClick={() => setIsOpen(false)} />
      </div>
      {props.mode === 'multiple' ? (
        <>
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="contacts__dropdown-chips">
            {visibleOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={classNames('contacts__chip', { 'contacts__chip--selected': props.value.includes(option.value) })}
                aria-pressed={props.value.includes(option.value)}
                onClick={() => toggle(option.value)}
              >
                {option.label}
              </button>
            ))}
            {visibleOptions.length === 0 && <span className="contacts__muted">Aucun résultat</span>}
          </div>
        </>
      ) : props.mode === 'starred' ? (
        <>
          <div className="contacts__dropdown-list">
            {options.map((option) => (
              <div
                key={option.value}
                className={classNames('contacts__dropdown-item', { 'contacts__dropdown-item--selected': option.value === props.value })}
              >
                <button
                  type="button"
                  className="contacts__dropdown-item-label"
                  aria-pressed={option.value === props.value}
                  onClick={() => {
                    props.onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  <span className="contacts__dropdown-check">{option.value === props.value && <CheckOutlined />}</span>
                  {option.label}
                </button>
                <button
                  type="button"
                  className={classNames('contacts__dropdown-star', { 'contacts__dropdown-star--active': option.value === props.starredValue })}
                  aria-label={`Définir ${option.label} par défaut`}
                  aria-pressed={option.value === props.starredValue}
                  onClick={() => props.onStar(option.value)}
                >
                  {option.value === props.starredValue ? <StarFilled /> : <StarOutlined />}
                </button>
              </div>
            ))}
          </div>
          <p className="contacts__dropdown-footer">{props.footer}</p>
        </>
      ) : (
        <Radio.Group
          className="contacts__dropdown-radios"
          value={props.value}
          options={options}
          onChange={(e) => {
            props.onChange(e.target.value);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setSearch('');
      }}
    >
      <Button className={classNames('contacts__filter-button', { 'contacts__filter-button--active': selectedCount > 0 })}>
        {label}
        {selectedCount > 0 && <span className="contacts__filter-count">{selectedCount}</span>}
        <DownOutlined className="contacts__filter-chevron" />
      </Button>
    </Popover>
  );
};

export default ContactsFilterDropdown;
